import { useState, useEffect, useCallback } from 'react';
import { Layout } from '../../components/Layout';
import { Card } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { apiKeysApi } from '../../api/apikeys';
import type { ApiKey, CreatedApiKey, Permission } from '../../api/apikeys';

// Get API base URL dynamically
const getApiBaseUrl = () => {
  if (typeof window !== 'undefined') {
    return `${window.location.origin}/api`;
  }
  return '/api';
};
import {
  Key,
  Plus,
  Eye,
  EyeOff,
  Copy,
  Check,
  Trash2,
  Power,
  AlertCircle,
  Book,
  Code,
  Download,
  ChevronDown,
  ChevronRight,
  Terminal,
  FileJson,
  Shield,
  X,
} from 'lucide-react';
import { clsx } from 'clsx';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { oneDark } from 'react-syntax-highlighter/dist/esm/styles/prism';

// Custom syntax theme based on cyberpunk aesthetic
const codeTheme = {
  ...oneDark,
  'pre[class*="language-"]': {
    ...oneDark['pre[class*="language-"]'],
    background: 'transparent',
    margin: 0,
    padding: 0,
    fontSize: '0.875rem',
    lineHeight: '1.6',
  },
  'code[class*="language-"]': {
    ...oneDark['code[class*="language-"]'],
    background: 'transparent',
    fontSize: '0.875rem',
  },
};

// Code block component with copy button
function CodeBlock({ code, language }: { code: string; language: string }) {
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    await navigator.clipboard.writeText(code);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="relative group">
      <div className="absolute top-3 right-3 z-10">
        <button
          onClick={handleCopy}
          className={clsx(
            'p-2 rounded-lg transition-all duration-200',
            copied
              ? 'bg-emerald-500/20 text-emerald-400'
              : 'bg-slate-800/80 text-slate-400 opacity-0 group-hover:opacity-100 hover:text-white hover:bg-slate-700'
          )}
          title="Copy code"
        >
          {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
        </button>
      </div>
      <div className="p-4 rounded-xl bg-slate-900/80 border border-slate-700/50 overflow-x-auto">
        <SyntaxHighlighter
          language={language}
          style={codeTheme}
          customStyle={{ background: 'transparent', margin: 0, padding: 0 }}
          wrapLines
          wrapLongLines
        >
          {code}
        </SyntaxHighlighter>
      </div>
    </div>
  );
}

// ============================================================================
// Types
// ============================================================================

interface NewKeyModal {
  isOpen: boolean;
  name: string;
  permissions: string[];
  isLoading: boolean;
}

// ============================================================================
// Main Component
// ============================================================================

export function ApiKeysPage() {
  const [apiKeys, setApiKeys] = useState<ApiKey[]>([]);
  const [permissions, setPermissions] = useState<Permission[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  // New key modal
  const [modal, setModal] = useState<NewKeyModal>({
    isOpen: false,
    name: '',
    permissions: [],
    isLoading: false,
  });
  
  // Newly created key (shown only once)
  const [newKey, setNewKey] = useState<CreatedApiKey | null>(null);
  const [showNewKey, setShowNewKey] = useState(false);
  const [copied, setCopied] = useState(false);
  
  // Delete confirmation
  const [deleteConfirm, setDeleteConfirm] = useState<string | null>(null);
  
  // Collapsible sections
  const [expandedSections, setExpandedSections] = useState({
    documentation: true,
    examples: false,
    postman: false,
  });
  
  // Selected code language for examples
  const [selectedLanguage, setSelectedLanguage] = useState<'curl' | 'typescript' | 'python'>('curl');

  // Load data
  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      const [keysData, permsData] = await Promise.all([
        apiKeysApi.list(),
        apiKeysApi.getPermissions(),
      ]);
      setApiKeys(keysData);
      setPermissions(permsData);
    } catch (err: unknown) {
      const error = err as { response?: { data?: { error?: string } } };
      setError(error.response?.data?.error || 'Failed to load API keys');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Create new key
  const handleCreateKey = async () => {
    if (!modal.name.trim()) return;
    
    setModal(prev => ({ ...prev, isLoading: true }));
    try {
      const key = await apiKeysApi.create({
        name: modal.name,
        permissions: modal.permissions,
      });
      setNewKey(key);
      setShowNewKey(true);
      setModal({ isOpen: false, name: '', permissions: [], isLoading: false });
      loadData();
    } catch (err: unknown) {
      const error = err as { response?: { data?: { error?: string } } };
      setError(error.response?.data?.error || 'Failed to create API key');
      setModal(prev => ({ ...prev, isLoading: false }));
    }
  };

  // Toggle key
  const handleToggle = async (id: string) => {
    try {
      await apiKeysApi.toggle(id);
      loadData();
    } catch (err: unknown) {
      const error = err as { response?: { data?: { error?: string } } };
      setError(error.response?.data?.error || 'Failed to toggle API key');
    }
  };

  // Revoke key
  const handleRevoke = async (id: string) => {
    try {
      await apiKeysApi.revoke(id);
      setDeleteConfirm(null);
      loadData();
    } catch (err: unknown) {
      const error = err as { response?: { data?: { error?: string } } };
      setError(error.response?.data?.error || 'Failed to revoke API key');
    }
  };

  // Copy to clipboard
  const handleCopy = async (text: string) => {
    await navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  // Toggle permission
  const togglePermission = (perm: string) => {
    setModal(prev => ({
      ...prev,
      permissions: prev.permissions.includes(perm)
        ? prev.permissions.filter(p => p !== perm)
        : [...prev.permissions, perm],
    }));
  };

  // Toggle section
  const toggleSection = (section: keyof typeof expandedSections) => {
    setExpandedSections(prev => ({ ...prev, [section]: !prev[section] }));
  };

  // Generate Postman collection
  const downloadPostmanCollection = () => {
    const collection = {
      info: {
        name: 'CipherBin API',
        description: 'API collection for CipherBin - Cipher-Grade Binary Protection',
        schema: 'https://schema.getpostman.com/json/collection/v2.1.0/collection.json',
      },
      auth: {
        type: 'apikey',
        apikey: [
          { key: 'key', value: 'X-API-Key', type: 'string' },
          { key: 'value', value: '{{api_key}}', type: 'string' },
          { key: 'in', value: 'header', type: 'string' },
        ],
      },
      variable: [
        { key: 'base_url', value: getApiBaseUrl(), type: 'string' },
        { key: 'api_key', value: 'your_api_key_here', type: 'string' },
      ],
      item: [
        {
          name: 'Builds',
          item: [
            {
              name: 'Upload File',
              request: {
                method: 'POST',
                header: [],
                body: {
                  mode: 'formdata',
                  formdata: [
                    { key: 'file', type: 'file', src: '' },
                  ],
                },
                url: { raw: '{{base_url}}/builds/upload', host: ['{{base_url}}'], path: ['builds', 'upload'] },
              },
            },
            {
              name: 'Start Build',
              request: {
                method: 'POST',
                header: [{ key: 'Content-Type', value: 'application/json' }],
                body: {
                  mode: 'raw',
                  raw: JSON.stringify({
                    uploadId: 'your_upload_id',
                    settings: {
                      packOutput: true,
                      importProtection: true,
                      memoryProtection: true,
                    },
                    zipPassword: true,
                  }, null, 2),
                },
                url: { raw: '{{base_url}}/builds/start', host: ['{{base_url}}'], path: ['builds', 'start'] },
              },
            },
            {
              name: 'Start Build (from Stored File)',
              request: {
                method: 'POST',
                header: [{ key: 'Content-Type', value: 'application/json' }],
                body: {
                  mode: 'raw',
                  raw: JSON.stringify({
                    storedFileId: 'your_stored_file_id',
                    settings: {
                      packOutput: true,
                      importProtection: true,
                      memoryProtection: true,
                    },
                    zipPassword: true,
                  }, null, 2),
                },
                url: { raw: '{{base_url}}/builds/start', host: ['{{base_url}}'], path: ['builds', 'start'] },
              },
            },
            {
              name: 'Get Build Status',
              request: {
                method: 'GET',
                url: { raw: '{{base_url}}/builds/:id/status', host: ['{{base_url}}'], path: ['builds', ':id', 'status'] },
              },
            },
            {
              name: 'Download Build',
              request: {
                method: 'GET',
                url: { raw: '{{base_url}}/builds/:id/download', host: ['{{base_url}}'], path: ['builds', ':id', 'download'] },
              },
            },
            {
              name: 'List Builds',
              request: {
                method: 'GET',
                url: { raw: '{{base_url}}/builds', host: ['{{base_url}}'], path: ['builds'] },
              },
            },
          ],
        },
        {
          name: 'Stored Files',
          item: [
            {
              name: 'Upload Stored File',
              request: {
                method: 'POST',
                header: [],
                body: {
                  mode: 'formdata',
                  formdata: [
                    { key: 'file', type: 'file', src: '', description: 'Binary file (.exe, .dll, .sys)' },
                    { key: 'pdb', type: 'file', src: '', description: 'Optional PDB file for function discovery' },
                  ],
                },
                url: { raw: '{{base_url}}/stored-files/upload', host: ['{{base_url}}'], path: ['stored-files', 'upload'] },
              },
            },
            {
              name: 'List Stored Files',
              request: {
                method: 'GET',
                url: { raw: '{{base_url}}/stored-files', host: ['{{base_url}}'], path: ['stored-files'] },
              },
            },
            {
              name: 'Get Stored File',
              request: {
                method: 'GET',
                url: { raw: '{{base_url}}/stored-files/:id', host: ['{{base_url}}'], path: ['stored-files', ':id'] },
              },
            },
            {
              name: 'Delete Stored File',
              request: {
                method: 'DELETE',
                url: { raw: '{{base_url}}/stored-files/:id', host: ['{{base_url}}'], path: ['stored-files', ':id'] },
              },
            },
            {
              name: 'Extend Stored File Expiry',
              request: {
                method: 'POST',
                url: { raw: '{{base_url}}/stored-files/:id/extend', host: ['{{base_url}}'], path: ['stored-files', ':id', 'extend'] },
              },
            },
          ],
        },
        {
          name: 'Configs',
          item: [
            {
              name: 'List Configs',
              request: { method: 'GET', url: { raw: '{{base_url}}/configs', host: ['{{base_url}}'], path: ['configs'] } },
            },
            {
              name: 'Create Config',
              request: {
                method: 'POST',
                header: [{ key: 'Content-Type', value: 'application/json' }],
                body: {
                  mode: 'raw',
                  raw: JSON.stringify({ name: 'My Config', engine: 'vmprotect', settings: {} }, null, 2),
                },
                url: { raw: '{{base_url}}/configs', host: ['{{base_url}}'], path: ['configs'] },
              },
            },
          ],
        },
        {
          name: 'Credits',
          item: [
            {
              name: 'Get Balance',
              request: { method: 'GET', url: { raw: '{{base_url}}/credits/balance', host: ['{{base_url}}'], path: ['credits', 'balance'] } },
            },
            {
              name: 'Get History',
              request: { method: 'GET', url: { raw: '{{base_url}}/credits/history', host: ['{{base_url}}'], path: ['credits', 'history'] } },
            },
          ],
        },
      ],
    };

    const blob = new Blob([JSON.stringify(collection, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'cipherbin-api.postman_collection.json';
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <Layout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-white flex items-center gap-2 sm:gap-3">
              <Key className="w-6 h-6 sm:w-8 sm:h-8 text-cyan-400" />
              API Keys
            </h1>
            <p className="text-slate-400 mt-1 text-sm sm:text-base">
              Manage your API keys for programmatic access
            </p>
          </div>
          <Button onClick={() => setModal(prev => ({ ...prev, isOpen: true }))} glow className="w-full sm:w-auto">
            <Plus className="w-4 h-4 mr-2" />
            Create New Key
          </Button>
        </div>

        {/* Error */}
        {error && (
          <div className="p-4 rounded-xl bg-red-500/10 border border-red-500/30 flex items-center gap-3">
            <AlertCircle className="w-5 h-5 text-red-400" />
            <span className="text-red-400">{error}</span>
            <button onClick={() => setError(null)} className="ml-auto text-red-400 hover:text-red-300">
              <X className="w-4 h-4" />
            </button>
          </div>
        )}

        {/* New Key Display (only shown once after creation) */}
        {newKey && (
          <Card title="New API Key Created" variant="glow" className="border-emerald-500/30 bg-emerald-500/5">
            <div className="space-y-4">
              <div className="p-4 rounded-lg bg-slate-900/50 border border-slate-700">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm text-slate-400">Your API Key</span>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => setShowNewKey(!showNewKey)}
                      className="p-1.5 rounded-lg hover:bg-slate-700 text-slate-400 hover:text-white transition-colors"
                    >
                      {showNewKey ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                    <button
                      onClick={() => handleCopy(newKey.key)}
                      className="p-1.5 rounded-lg hover:bg-slate-700 text-slate-400 hover:text-white transition-colors"
                    >
                      {copied ? <Check className="w-4 h-4 text-emerald-400" /> : <Copy className="w-4 h-4" />}
                    </button>
                  </div>
                </div>
                <code className="font-mono text-lg text-cyan-400 break-all">
                  {showNewKey ? newKey.key : '•'.repeat(40)}
                </code>
              </div>
              <div className="flex items-center gap-2 text-amber-400 text-sm">
                <AlertCircle className="w-4 h-4" />
                <span>Save this key now! It won't be shown again.</span>
              </div>
              <Button variant="secondary" onClick={() => setNewKey(null)}>
                I've saved my key
              </Button>
            </div>
          </Card>
        )}

        {/* API Keys Table */}
        <Card title="Your API Keys" subtitle={`${apiKeys.length} key${apiKeys.length !== 1 ? 's' : ''}`}>
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="w-8 h-8 border-2 border-cyan-500 border-t-transparent rounded-full animate-spin" />
            </div>
          ) : apiKeys.length === 0 ? (
            <div className="text-center py-12">
              <Key className="w-12 h-12 text-slate-600 mx-auto mb-4" />
              <p className="text-slate-400">No API keys yet</p>
              <p className="text-sm text-slate-500 mt-1">Create your first API key to get started</p>
            </div>
          ) : (
            <>
              {/* Mobile Card Layout */}
              <div className="lg:hidden space-y-3">
                {apiKeys.map((key) => (
                  <div
                    key={key.id}
                    className="p-4 rounded-xl bg-slate-800/30 border border-slate-700/50 space-y-3"
                  >
                    {/* Header with name and status */}
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0 flex-1">
                        <p className="font-medium text-white truncate">{key.name}</p>
                        <code className="font-mono text-xs text-cyan-400 bg-slate-800/50 px-2 py-0.5 rounded mt-1 inline-block">
                          {key.keyPrefix}...
                        </code>
                      </div>
                      <span className={clsx(
                        'inline-flex items-center gap-1.5 px-2 py-1 rounded-full text-xs font-medium flex-shrink-0',
                        key.isActive
                          ? 'bg-emerald-500/10 text-emerald-400 border border-emerald-500/30'
                          : 'bg-slate-500/10 text-slate-400 border border-slate-500/30'
                      )}>
                        <span className={clsx(
                          'w-1.5 h-1.5 rounded-full',
                          key.isActive ? 'bg-emerald-400' : 'bg-slate-400'
                        )} />
                        {key.isActive ? 'Active' : 'Inactive'}
                      </span>
                    </div>
                    
                    {/* Permissions */}
                    <div className="flex flex-wrap gap-1">
                      {key.permissions.length === 0 ? (
                        <span className="text-slate-500 text-xs">No permissions</span>
                      ) : (
                        key.permissions.map((perm) => (
                          <span
                            key={perm}
                            className="px-2 py-0.5 rounded bg-purple-500/10 text-purple-400 text-xs"
                          >
                            {perm}
                          </span>
                        ))
                      )}
                    </div>
                    
                    {/* Meta info */}
                    <div className="flex items-center gap-4 text-xs text-slate-500">
                      <span>Created: {new Date(key.createdAt).toLocaleDateString()}</span>
                      <span>Last used: {key.lastUsedAt ? new Date(key.lastUsedAt).toLocaleDateString() : 'Never'}</span>
                    </div>
                    
                    {/* Actions */}
                    <div className="flex items-center gap-2 pt-2 border-t border-slate-700/50">
                      <button
                        onClick={() => handleToggle(key.id)}
                        className={clsx(
                          'flex-1 flex items-center justify-center gap-2 py-2 rounded-lg transition-colors text-sm font-medium',
                          key.isActive
                            ? 'bg-amber-500/10 text-amber-400 hover:bg-amber-500/20'
                            : 'bg-emerald-500/10 text-emerald-400 hover:bg-emerald-500/20'
                        )}
                      >
                        <Power className="w-4 h-4" />
                        {key.isActive ? 'Deactivate' : 'Activate'}
                      </button>
                      {deleteConfirm === key.id ? (
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => handleRevoke(key.id)}
                            className="px-4 py-2 rounded-lg bg-red-500 text-white text-sm font-medium hover:bg-red-600 transition-colors"
                          >
                            Confirm
                          </button>
                          <button
                            onClick={() => setDeleteConfirm(null)}
                            className="px-4 py-2 rounded-lg bg-slate-700 text-white text-sm font-medium hover:bg-slate-600 transition-colors"
                          >
                            Cancel
                          </button>
                        </div>
                      ) : (
                        <button
                          onClick={() => setDeleteConfirm(key.id)}
                          className="flex items-center justify-center gap-2 px-4 py-2 rounded-lg bg-red-500/10 text-red-400 hover:bg-red-500/20 transition-colors text-sm font-medium"
                        >
                          <Trash2 className="w-4 h-4" />
                          Revoke
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
              
              {/* Desktop Table Layout */}
              <div className="hidden lg:block overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-slate-700/50">
                      <th className="text-left py-3 px-4 text-sm font-medium text-slate-400">Name</th>
                      <th className="text-left py-3 px-4 text-sm font-medium text-slate-400">Key Prefix</th>
                      <th className="text-left py-3 px-4 text-sm font-medium text-slate-400">Status</th>
                      <th className="text-left py-3 px-4 text-sm font-medium text-slate-400">Permissions</th>
                      <th className="text-left py-3 px-4 text-sm font-medium text-slate-400">Last Used</th>
                      <th className="text-left py-3 px-4 text-sm font-medium text-slate-400">Created</th>
                      <th className="text-right py-3 px-4 text-sm font-medium text-slate-400">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {apiKeys.map((key) => (
                      <tr key={key.id} className="border-b border-slate-800/50 hover:bg-slate-800/30 transition-colors">
                        <td className="py-3 px-4">
                          <span className="font-medium text-white">{key.name}</span>
                        </td>
                        <td className="py-3 px-4">
                          <code className="font-mono text-sm text-cyan-400 bg-slate-800/50 px-2 py-1 rounded">
                            {key.keyPrefix}...
                          </code>
                        </td>
                        <td className="py-3 px-4">
                          <span className={clsx(
                            'inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium',
                            key.isActive
                              ? 'bg-emerald-500/10 text-emerald-400 border border-emerald-500/30'
                              : 'bg-slate-500/10 text-slate-400 border border-slate-500/30'
                          )}>
                            <span className={clsx(
                              'w-1.5 h-1.5 rounded-full',
                              key.isActive ? 'bg-emerald-400' : 'bg-slate-400'
                            )} />
                            {key.isActive ? 'Active' : 'Inactive'}
                          </span>
                        </td>
                        <td className="py-3 px-4">
                          <div className="flex flex-wrap gap-1">
                            {key.permissions.length === 0 ? (
                              <span className="text-slate-500 text-sm">None</span>
                            ) : (
                              key.permissions.slice(0, 2).map((perm) => (
                                <span
                                  key={perm}
                                  className="px-2 py-0.5 rounded bg-purple-500/10 text-purple-400 text-xs"
                                >
                                  {perm}
                                </span>
                              ))
                            )}
                            {key.permissions.length > 2 && (
                              <span className="px-2 py-0.5 rounded bg-slate-700 text-slate-400 text-xs">
                                +{key.permissions.length - 2}
                              </span>
                            )}
                          </div>
                        </td>
                        <td className="py-3 px-4 text-sm text-slate-400">
                          {key.lastUsedAt
                            ? new Date(key.lastUsedAt).toLocaleDateString()
                            : 'Never'}
                        </td>
                        <td className="py-3 px-4 text-sm text-slate-400">
                          {new Date(key.createdAt).toLocaleDateString()}
                        </td>
                        <td className="py-3 px-4">
                          <div className="flex items-center justify-end gap-2">
                            <button
                              onClick={() => handleToggle(key.id)}
                              className={clsx(
                                'p-2 rounded-lg transition-colors',
                                key.isActive
                                  ? 'hover:bg-amber-500/10 text-amber-400'
                                  : 'hover:bg-emerald-500/10 text-emerald-400'
                              )}
                              title={key.isActive ? 'Deactivate' : 'Activate'}
                            >
                              <Power className="w-4 h-4" />
                            </button>
                            {deleteConfirm === key.id ? (
                              <div className="flex items-center gap-1">
                                <button
                                  onClick={() => handleRevoke(key.id)}
                                  className="px-2 py-1 rounded bg-red-500 text-white text-xs hover:bg-red-600 transition-colors"
                                >
                                  Confirm
                                </button>
                                <button
                                  onClick={() => setDeleteConfirm(null)}
                                  className="px-2 py-1 rounded bg-slate-700 text-white text-xs hover:bg-slate-600 transition-colors"
                                >
                                  Cancel
                                </button>
                              </div>
                            ) : (
                              <button
                                onClick={() => setDeleteConfirm(key.id)}
                                className="p-2 rounded-lg hover:bg-red-500/10 text-red-400 transition-colors"
                                title="Revoke Key"
                              >
                                <Trash2 className="w-4 h-4" />
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </Card>

        {/* Documentation Section */}
        <Card>
          <button
            onClick={() => toggleSection('documentation')}
            className="w-full flex items-center justify-between p-4 -m-6 mb-0 hover:bg-slate-800/30 transition-colors rounded-t-2xl"
          >
            <div className="flex items-center gap-3">
              <Book className="w-5 h-5 text-cyan-400" />
              <span className="font-semibold text-white">API Documentation</span>
            </div>
            {expandedSections.documentation ? (
              <ChevronDown className="w-5 h-5 text-slate-400" />
            ) : (
              <ChevronRight className="w-5 h-5 text-slate-400" />
            )}
          </button>
          
          {expandedSections.documentation && (
            <div className="mt-6 space-y-6">
              {/* Base URL */}
              <div>
                <h3 className="text-sm font-medium text-slate-400 mb-2">Base URL</h3>
                <code className="block p-3 rounded-lg bg-slate-900/50 border border-slate-700 font-mono text-cyan-400">
                  {getApiBaseUrl()}
                </code>
              </div>

              {/* Authentication */}
              <div>
                <h3 className="text-sm font-medium text-slate-400 mb-2">Authentication</h3>
                <p className="text-slate-300 mb-3">
                  Include your API key in the <code className="text-cyan-400 bg-slate-800 px-1.5 py-0.5 rounded">X-API-Key</code> header:
                </p>
                <code className="block p-3 rounded-lg bg-slate-900/50 border border-slate-700 font-mono text-sm">
                  <span className="text-purple-400">X-API-Key:</span>{' '}
                  <span className="text-emerald-400">vmp_xxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx</span>
                </code>
              </div>

              {/* Endpoints */}
              <div>
                <h3 className="text-sm font-medium text-slate-400 mb-3">Endpoints</h3>
                <div className="space-y-2">
                  {[
                    { method: 'POST', path: '/builds/upload', desc: 'Upload a file for processing' },
                    { method: 'POST', path: '/builds/start', desc: 'Start build (uploadId or storedFileId)' },
                    { method: 'GET', path: '/builds/:id/status', desc: 'Get build status & zip password' },
                    { method: 'GET', path: '/builds/:id/download', desc: 'Download protected zip file' },
                    { method: 'GET', path: '/builds', desc: 'List all builds' },
                    { method: 'POST', path: '/stored-files/upload', desc: 'Store file for reuse (+ PDB)' },
                    { method: 'GET', path: '/stored-files', desc: 'List stored files' },
                    { method: 'GET', path: '/stored-files/:id', desc: 'Get stored file details' },
                    { method: 'DELETE', path: '/stored-files/:id', desc: 'Delete stored file' },
                    { method: 'POST', path: '/stored-files/:id/extend', desc: 'Extend expiry (30 days)' },
                    { method: 'GET', path: '/credits/balance', desc: 'Get credit balance' },
                    { method: 'GET', path: '/configs', desc: 'List saved configs' },
                    { method: 'POST', path: '/configs', desc: 'Create new config' },
                  ].map((endpoint) => (
                    <div
                      key={endpoint.path}
                      className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-3 p-3 rounded-lg bg-slate-800/30 border border-slate-700/50"
                    >
                      <div className="flex items-center gap-2 sm:gap-3">
                        <span className={clsx(
                          'px-2 py-1 rounded text-xs font-bold uppercase flex-shrink-0',
                          endpoint.method === 'GET' ? 'bg-emerald-500/20 text-emerald-400' : 'bg-cyan-500/20 text-cyan-400'
                        )}>
                          {endpoint.method}
                        </span>
                        <code className="font-mono text-sm text-white break-all">{endpoint.path}</code>
                      </div>
                      <span className="text-xs sm:text-sm text-slate-400 sm:ml-auto">{endpoint.desc}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Response Format */}
              <div>
                <h3 className="text-sm font-medium text-slate-400 mb-2">Response Format</h3>
                <p className="text-slate-300 mb-3">All responses follow this structure:</p>
                <CodeBlock
                  language="json"
                  code={`{
  "success": true,
  "data": { ... },
  "message": "Optional message"
}`}
                />
              </div>

              {/* Error Codes */}
              <div>
                <h3 className="text-sm font-medium text-slate-400 mb-3">Error Codes</h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  {[
                    { code: 400, name: 'Bad Request', desc: 'Invalid input data' },
                    { code: 401, name: 'Unauthorized', desc: 'Missing or invalid API key' },
                    { code: 403, name: 'Forbidden', desc: 'Insufficient permissions' },
                    { code: 404, name: 'Not Found', desc: 'Resource not found' },
                    { code: 429, name: 'Rate Limited', desc: 'Too many requests' },
                    { code: 500, name: 'Server Error', desc: 'Internal server error' },
                  ].map((err) => (
                    <div
                      key={err.code}
                      className="flex items-start sm:items-center gap-3 p-3 rounded-lg bg-slate-800/30 border border-slate-700/50"
                    >
                      <span className="px-2 py-1 rounded bg-red-500/20 text-red-400 text-xs font-bold flex-shrink-0">
                        {err.code}
                      </span>
                      <div className="min-w-0">
                        <span className="text-white font-medium text-sm">{err.name}</span>
                        <span className="text-slate-400 text-xs sm:text-sm block sm:inline sm:ml-2">{err.desc}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </Card>

        {/* Code Examples Section */}
        <Card>
          <button
            onClick={() => toggleSection('examples')}
            className="w-full flex items-center justify-between p-4 -m-6 mb-0 hover:bg-slate-800/30 transition-colors rounded-t-2xl"
          >
            <div className="flex items-center gap-3">
              <Code className="w-5 h-5 text-purple-400" />
              <span className="font-semibold text-white">Code Examples</span>
            </div>
            {expandedSections.examples ? (
              <ChevronDown className="w-5 h-5 text-slate-400" />
            ) : (
              <ChevronRight className="w-5 h-5 text-slate-400" />
            )}
          </button>
          
          {expandedSections.examples && (
            <div className="mt-6 space-y-4">
              {/* Language Selector */}
              <div className="flex flex-col sm:flex-row gap-2 p-1.5 rounded-xl bg-slate-800/50 border border-slate-700/50">
                <button
                  onClick={() => setSelectedLanguage('curl')}
                  className={clsx(
                    'flex items-center gap-2 px-3 sm:px-4 py-2.5 rounded-lg font-medium transition-all duration-200 flex-1 justify-center text-sm sm:text-base',
                    selectedLanguage === 'curl'
                      ? 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/30'
                      : 'text-slate-400 hover:text-white hover:bg-slate-700/50 border border-transparent'
                  )}
                >
                  <Terminal className="w-4 h-4" />
                  <span>cURL</span>
                </button>
                <button
                  onClick={() => setSelectedLanguage('typescript')}
                  className={clsx(
                    'flex items-center gap-2 px-3 sm:px-4 py-2.5 rounded-lg font-medium transition-all duration-200 flex-1 justify-center text-sm sm:text-base',
                    selectedLanguage === 'typescript'
                      ? 'bg-yellow-500/20 text-yellow-400 border border-yellow-500/30'
                      : 'text-slate-400 hover:text-white hover:bg-slate-700/50 border border-transparent'
                  )}
                >
                  <FileJson className="w-4 h-4" />
                  <span>TypeScript</span>
                </button>
                <button
                  onClick={() => setSelectedLanguage('python')}
                  className={clsx(
                    'flex items-center gap-2 px-3 sm:px-4 py-2.5 rounded-lg font-medium transition-all duration-200 flex-1 justify-center text-sm sm:text-base',
                    selectedLanguage === 'python'
                      ? 'bg-blue-500/20 text-blue-400 border border-blue-500/30'
                      : 'text-slate-400 hover:text-white hover:bg-slate-700/50 border border-transparent'
                  )}
                >
                  <Code className="w-4 h-4" />
                  <span>Python</span>
                </button>
              </div>

              {/* cURL Example */}
              {selectedLanguage === 'curl' && (
                <CodeBlock
                  language="bash"
                  code={`# ===== Option A: Direct Upload =====

# 1. Upload file
curl -X POST ${getApiBaseUrl()}/builds/upload \\
  -H "X-API-Key: YOUR_API_KEY" \\
  -F "file=@myapp.exe"

# Response: { "success": true, "data": { "uploadId": "abc123..." } }

# 2. Start build with zip password
curl -X POST ${getApiBaseUrl()}/builds/start \\
  -H "X-API-Key: YOUR_API_KEY" \\
  -H "Content-Type: application/json" \\
  -d '{
    "uploadId": "abc123...",
    "settings": { "packOutput": true, "importProtection": true },
    "zipPassword": true
  }'

# Response includes: { "data": { "id": "...", "zipPassword": "A3x#9..." } }

# ===== Option B: Store File for Reuse =====

# 1. Store file with PDB for function discovery
curl -X POST ${getApiBaseUrl()}/stored-files/upload \\
  -H "X-API-Key: YOUR_API_KEY" \\
  -F "file=@myapp.exe" \\
  -F "pdb=@myapp.pdb"

# Response: { "data": { "id": "stored123...", "hasPdb": true, "functionsCount": 500 } }

# 2. Start build from stored file
curl -X POST ${getApiBaseUrl()}/builds/start \\
  -H "X-API-Key: YOUR_API_KEY" \\
  -H "Content-Type: application/json" \\
  -d '{
    "storedFileId": "stored123...",
    "settings": { "packOutput": true },
    "zipPassword": true
  }'

# 3. Check status (includes zipPassword)
curl ${getApiBaseUrl()}/builds/BUILD_ID/status \\
  -H "X-API-Key: YOUR_API_KEY"

# 4. Download zip (use password from status response)
curl -O ${getApiBaseUrl()}/builds/BUILD_ID/download \\
  -H "X-API-Key: YOUR_API_KEY"`}
                />
              )}

              {/* TypeScript Example */}
              {selectedLanguage === 'typescript' && (
                <CodeBlock
                  language="typescript"
                  code={`const API_KEY = 'your_api_key';
const BASE_URL = '${getApiBaseUrl()}';

// Store a file for reuse (upload once, build many times)
async function storeFile(filePath: string, pdbPath?: string) {
  const formData = new FormData();
  formData.append('file', fs.createReadStream(filePath));
  if (pdbPath) formData.append('pdb', fs.createReadStream(pdbPath));
  
  const res = await fetch(\`\${BASE_URL}/stored-files/upload\`, {
    method: 'POST',
    headers: { 'X-API-Key': API_KEY },
    body: formData,
  });
  const { data } = await res.json();
  console.log(\`Stored: \${data.id}, PDB: \${data.hasPdb}, Functions: \${data.functionsCount}\`);
  return data.id; // storedFileId for future builds
}

// Build from stored file with password-protected zip output
async function protectFromStored(storedFileId: string) {
  // 1. Start build from stored file
  const buildRes = await fetch(\`\${BASE_URL}/builds/start\`, {
    method: 'POST',
    headers: { 'X-API-Key': API_KEY, 'Content-Type': 'application/json' },
    body: JSON.stringify({
      storedFileId,
      settings: { packOutput: true, importProtection: true },
      zipPassword: true, // Request password-protected zip
    }),
  });
  const { data: { id: buildId, zipPassword } } = await buildRes.json();
  console.log(\`Build started: \${buildId}, Zip password: \${zipPassword}\`);

  // 2. Poll for completion
  let status = 'PROCESSING';
  while (status === 'PROCESSING' || status === 'PENDING') {
    await new Promise(r => setTimeout(r, 2000));
    const statusRes = await fetch(\`\${BASE_URL}/builds/\${buildId}/status\`, {
      headers: { 'X-API-Key': API_KEY },
    });
    const result = await statusRes.json();
    status = result.data.status;
  }

  // 3. Download password-protected zip
  if (status === 'COMPLETED') {
    const downloadRes = await fetch(\`\${BASE_URL}/builds/\${buildId}/download\`, {
      headers: { 'X-API-Key': API_KEY },
    });
    return { blob: await downloadRes.blob(), zipPassword };
  }
}

// Usage
const storedId = await storeFile('myapp.exe', 'myapp.pdb');
const { blob, zipPassword } = await protectFromStored(storedId);
console.log(\`Download ready! Zip password: \${zipPassword}\`);`}
                />
              )}

              {/* Python Example */}
              {selectedLanguage === 'python' && (
                <CodeBlock
                  language="python"
                  code={`import requests
import time

API_KEY = 'your_api_key'
BASE_URL = '${getApiBaseUrl()}'
headers = {'X-API-Key': API_KEY}

def store_file(file_path: str, pdb_path: str = None) -> str:
    """Store a file for reuse. Optionally include PDB for function discovery."""
    files = {'file': open(file_path, 'rb')}
    if pdb_path:
        files['pdb'] = open(pdb_path, 'rb')
    
    res = requests.post(f'{BASE_URL}/stored-files/upload', headers=headers, files=files)
    data = res.json()['data']
    print(f"Stored: {data['id']}, PDB: {data.get('hasPdb')}, Functions: {data.get('functionsCount')}")
    return data['id']

def protect_from_stored(stored_file_id: str, output_path: str) -> str:
    """Build from stored file with password-protected zip output."""
    # 1. Start build
    build_res = requests.post(
        f'{BASE_URL}/builds/start',
        headers={**headers, 'Content-Type': 'application/json'},
        json={
            'storedFileId': stored_file_id,
            'settings': {'packOutput': True, 'importProtection': True},
            'zipPassword': True,  # Request password-protected zip
        }
    )
    data = build_res.json()['data']
    build_id = data['id']
    zip_password = data.get('zipPassword')
    print(f"Build started: {build_id}, Zip password: {zip_password}")

    # 2. Poll for completion
    while True:
        status_res = requests.get(f'{BASE_URL}/builds/{build_id}/status', headers=headers)
        result = status_res.json()['data']
        status = result['status']
        
        if status == 'COMPLETED':
            break
        elif status == 'FAILED':
            raise Exception(f"Build failed: {result.get('error')}")
        time.sleep(2)

    # 3. Download password-protected zip
    download_res = requests.get(f'{BASE_URL}/builds/{build_id}/download', headers=headers)
    with open(output_path, 'wb') as f:
        f.write(download_res.content)
    
    return zip_password

# Usage: Store once, build many times
stored_id = store_file('myapp.exe', 'myapp.pdb')
password = protect_from_stored(stored_id, 'output.zip')
print(f"Downloaded! Use password: {password}")`}
                />
              )}
            </div>
          )}
        </Card>

        {/* Postman Section */}
        <Card>
          <button
            onClick={() => toggleSection('postman')}
            className="w-full flex items-center justify-between p-4 -m-6 mb-0 hover:bg-slate-800/30 transition-colors rounded-t-2xl"
          >
            <div className="flex items-center gap-3">
              <Download className="w-5 h-5 text-orange-400" />
              <span className="font-semibold text-white">Postman Collection</span>
            </div>
            {expandedSections.postman ? (
              <ChevronDown className="w-5 h-5 text-slate-400" />
            ) : (
              <ChevronRight className="w-5 h-5 text-slate-400" />
            )}
          </button>
          
          {expandedSections.postman && (
            <div className="mt-6 space-y-4">
              <p className="text-slate-300">
                Download a pre-configured Postman collection with all API endpoints ready to use.
              </p>
              <div className="flex items-start gap-4 p-4 rounded-lg bg-slate-800/30 border border-slate-700/50">
                <div className="p-3 rounded-xl bg-orange-500/10">
                  <FileJson className="w-8 h-8 text-orange-400" />
                </div>
                <div className="flex-1">
                  <h4 className="font-medium text-white">CipherBin API Collection</h4>
                  <p className="text-sm text-slate-400 mt-1">
                    Includes all endpoints with example requests and environment variables
                  </p>
                  <Button onClick={downloadPostmanCollection} variant="secondary" className="mt-3">
                    <Download className="w-4 h-4 mr-2" />
                    Download Collection
                  </Button>
                </div>
              </div>
              <div className="text-sm text-slate-400">
                <p className="font-medium text-slate-300 mb-2">After importing:</p>
                <ol className="list-decimal list-inside space-y-1">
                  <li>Set the <code className="text-cyan-400">api_key</code> variable to your API key</li>
                  <li>Update <code className="text-cyan-400">base_url</code> if using a different server</li>
                  <li>Start making requests!</li>
                </ol>
              </div>
            </div>
          )}
        </Card>

        {/* Create Key Modal */}
        {modal.isOpen && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 overflow-y-auto">
            <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={() => setModal(prev => ({ ...prev, isOpen: false }))} />
            <div className="relative w-full max-w-lg p-4 sm:p-6 rounded-2xl bg-slate-900 border border-slate-700 shadow-2xl my-auto">
              <div className="flex items-center justify-between mb-4 sm:mb-6">
                <h2 className="text-lg sm:text-xl font-bold text-white flex items-center gap-2">
                  <Key className="w-5 h-5 text-cyan-400" />
                  Create New API Key
                </h2>
                <button
                  onClick={() => setModal(prev => ({ ...prev, isOpen: false }))}
                  className="p-2 rounded-lg hover:bg-slate-800 text-slate-400 hover:text-white transition-colors"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-slate-400 mb-2">
                    Key Name
                  </label>
                  <Input
                    placeholder="e.g., Production Server"
                    value={modal.name}
                    onChange={(e) => setModal(prev => ({ ...prev, name: e.target.value }))}
                    icon={<Key className="w-4 h-4" />}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-400 mb-2">
                    Permissions
                  </label>
                  <div className="space-y-2 max-h-[40vh] overflow-y-auto">
                    {permissions.map((perm) => (
                      <label
                        key={perm.permission}
                        className={clsx(
                          'flex items-start sm:items-center gap-3 p-3 rounded-lg border cursor-pointer transition-all',
                          modal.permissions.includes(perm.permission)
                            ? 'border-cyan-500/50 bg-cyan-500/10'
                            : 'border-slate-700 hover:border-slate-600 bg-slate-800/30'
                        )}
                      >
                        <input
                          type="checkbox"
                          checked={modal.permissions.includes(perm.permission)}
                          onChange={() => togglePermission(perm.permission)}
                          className="hidden"
                        />
                        <div className={clsx(
                          'w-5 h-5 rounded border-2 flex items-center justify-center transition-colors flex-shrink-0 mt-0.5 sm:mt-0',
                          modal.permissions.includes(perm.permission)
                            ? 'border-cyan-500 bg-cyan-500'
                            : 'border-slate-600'
                        )}>
                          {modal.permissions.includes(perm.permission) && (
                            <Check className="w-3 h-3 text-white" />
                          )}
                        </div>
                        <div className="min-w-0">
                          <span className="font-medium text-white text-sm sm:text-base">{perm.permission}</span>
                          <p className="text-xs sm:text-sm text-slate-400">{perm.description}</p>
                        </div>
                      </label>
                    ))}
                  </div>
                </div>
              </div>

              <div className="flex flex-col-reverse sm:flex-row gap-3 mt-6">
                <Button
                  variant="secondary"
                  className="flex-1"
                  onClick={() => setModal(prev => ({ ...prev, isOpen: false }))}
                >
                  Cancel
                </Button>
                <Button
                  className="flex-1"
                  onClick={handleCreateKey}
                  disabled={!modal.name.trim() || modal.isLoading}
                  glow
                >
                  {modal.isLoading ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin mr-2" />
                      Creating...
                    </>
                  ) : (
                    <>
                      <Shield className="w-4 h-4 mr-2" />
                      Create Key
                    </>
                  )}
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
}

